import { useCallback, useMemo, useRef, useState } from "react";

import {
  getMcapLicensingOverview,
  getMcapLicensingSchools,
  getMcapLicensingActivitySchools,
} from "api/api.service";
import useLicensingQuery from "../licensing/useLicensingQuery";
import LicensingHero from "../licensing/LicensingHero";
import LifecycleFlow from "../licensing/LifecycleFlow";
import TrialCountdown from "../licensing/TrialCountdown";
import PlanRings from "../licensing/PlanRings";
import RenewalRunway from "../licensing/RenewalRunway";
import RevenueChart from "../licensing/RevenueChart";
import SchoolLandscape from "../licensing/SchoolLandscape";
import ActivityBySchool from "../licensing/ActivityBySchool";
import LicensingDialog from "../licensing/LicensingDialog";
import { formatDateTime, formatNumber } from "../licensing/licensingTheme";
import "../licensing/licensing.scss";

/**
 * Payments & Licensing.
 *
 * Who is paying, who is about to stop, and who is still deciding — with the
 * people behind every number one click away. Lifecycle counts are always
 * "now"; revenue and conversion follow the date range; the school picker
 * scopes the whole screen.
 */
export default function PaymentsAndLicensing({ filterBar, filters }) {
  const scope = useRef(null);
  // A stack, so a drill-down opened from inside another (at-risk → the teachers
  // behind one kind of risk) can go back instead of only closing.
  const [drills, setDrills] = useState([]);
  const [schoolQuery, setSchoolQuery] = useState({ sort: "demand", order: "desc", search: "", activeOnly: true, page: 1 });

  const filtersKey = JSON.stringify(filters ?? {});
  // sectionId scopes a classroom; a subscription belongs to a teacher.
  const scopedFilters = useMemo(() => {
    const { sectionId, ...rest } = filters ?? {};
    return rest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey]);

  const overview = useLicensingQuery(getMcapLicensingOverview, "overview", scopedFilters, { cache: true });
  const schoolParams = useMemo(
    () => ({
      ...scopedFilters,
      sort: schoolQuery.sort,
      order: schoolQuery.order,
      activeOnly: String(schoolQuery.activeOnly),
      page: String(schoolQuery.page),
      limit: "25",
      ...(schoolQuery.search ? { search: schoolQuery.search } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtersKey, schoolQuery],
  );
  const schools = useLicensingQuery(getMcapLicensingSchools, "schools", schoolParams, { cache: true });
  const activity = useLicensingQuery(getMcapLicensingActivitySchools, "activity-schools", scopedFilters);

  const openDrill = useCallback((next, { fromRisk = false } = {}) => {
    setDrills((stack) => (fromRisk ? [...stack, next] : [next]));
  }, []);
  const closeDrill = useCallback(() => setDrills([]), []);
  const backDrill = useCallback(() => setDrills((stack) => stack.slice(0, -1)), []);
  const updateSchoolQuery = useCallback((patch) => setSchoolQuery((q) => ({ ...q, ...patch })), []);

  const data = overview.data;
  const drill = drills[drills.length - 1];

  return (
    <div className="p-4 mt-3 mcap-lic" ref={scope}>
      {filterBar}

      {overview.error && (
        <div className="mcap-lic-error-card" role="alert">
          <h3>This screen could not load.</h3>
          <p>Nothing is missing from your data — the request failed. {overview.error.message || ""}</p>
          <button type="button" className="mcap-lic-btn" onClick={overview.reload}>
            Try again
          </button>
        </div>
      )}

      {!overview.error && (
        <>
          <LicensingHero overview={data} loading={overview.loading} onDrill={openDrill} />

          <div>
            <LifecycleFlow overview={data} loading={overview.loading} onDrill={openDrill} />
          </div>

          <div className="mcap-lic-row">
            <TrialCountdown countdown={data?.trialCountdown} loading={overview.loading} onDrill={openDrill} />
            <PlanRings plans={data?.plans} loading={overview.loading} onDrill={openDrill} />
          </div>

          <div>
            <RenewalRunway runway={data?.runway} currency={data?.currency} loading={overview.loading} onDrill={openDrill} />
          </div>

          <div>
            <RevenueChart overview={data} loading={overview.loading} onDrill={openDrill} />
          </div>

          <div>
            <SchoolLandscape
              schools={schools.data}
              loading={schools.loading}
              error={schools.error}
              query={schoolQuery}
              onQueryChange={updateSchoolQuery}
              onDrill={openDrill}
            />
          </div>

          <div>
            <ActivityBySchool summary={activity.data} loading={activity.loading} error={activity.error} onDrill={openDrill} />
          </div>

          {data?.asOf && (
            <p className="mcap-lic-asof">
              Subscription counts as of {formatDateTime(data.asOf)}
              {data.checks && !data.checks.billingStatesSumToPopulation && (
                <>
                  {" · "}
                  <strong>
                    {formatNumber(data.checks.billingStateTotal)} states against {formatNumber(data.checks.population)}{" "}
                    teachers — these should match; please report it.
                  </strong>
                </>
              )}
            </p>
          )}
        </>
      )}

      {drill && (
        <LicensingDialog
          // Keyed on the drill itself, so moving from one list to another starts
          // on page one with default sorting instead of inheriting the last one's.
          key={JSON.stringify(drill)}
          drill={drill}
          filters={scopedFilters}
          risk={data?.risk}
          onClose={closeDrill}
          onDrill={openDrill}
          onBack={drills.length > 1 ? backDrill : undefined}
        />
      )}
    </div>
  );
}
